import { isWebView } from '../is-webview';
import type { GroupName } from './one-trust-categorised-cookies';
import {
  getGroupByGroupName,
  getGroupsFromOtCookieWhenAvailable,
  isValidConsentType,
  isValidGroupCode,
} from './one-trust-cookie-helpers';

export type ConsentResult =
  | {
      result: 'unknown';
      reason: 'Waiting for OT to be available';
    }
  | {
      result: 'given';
      reason:
        | 'Consent is not required'
        | 'Consent required and given'
        | 'OT is not enabled/ready'
        | 'OT is not available but consent is given to handle old version of mobile app';
    }
  | {
      result: 'not-given';
      reason:
        | 'OT cookie has no groups'
        | 'Cannot find consent for this group name in OT cookie'
        | 'Consent not given by the user'
        | 'OT cookie not found or no groups found in OT cookie and this group name requires user consent';
    };

export const getClientConsentByGroupName = async (
  groupName: GroupName
): Promise<ConsentResult> => {
  const { isConsentRequired, groupCode } = getGroupByGroupName(groupName);

  if (!isConsentRequired) {
    return {
      result: 'given',
      reason: 'Consent is not required',
    };
  }

  const otGroups = await getGroupsFromOtCookieWhenAvailable();

  if (!otGroups && isWebView()) {
    return {
      result: 'given',
      reason:
        'OT is not available but consent is given to handle old version of mobile app',
    };
  }

  if (!otGroups) {
    return {
      result: 'not-given',
      reason: 'OT cookie has no groups',
    };
  }

  const foundGroupCodeWithConsent = otGroups.find((group) =>
    group.includes(groupCode)
  );

  if (!foundGroupCodeWithConsent) {
    return {
      result: 'not-given',
      reason: 'Cannot find consent for this group name in OT cookie',
    };
  }

  const [foundGroupCode, foundConsentType] =
    foundGroupCodeWithConsent.split(':');

  if (
    !isValidGroupCode(foundGroupCode) ||
    !isValidConsentType(foundConsentType)
  ) {
    return {
      result: 'not-given',
      reason:
        'OT cookie not found or no groups found in OT cookie and this group name requires user consent',
    };
  }

  if (foundConsentType === '0') {
    return {
      result: 'not-given',
      reason: 'Consent not given by the user',
    };
  }

  return {
    result: 'given',
    reason: 'Consent required and given',
  };
};
